import React, { useMemo, useState } from 'react';
import { Predecessor, TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { DEP_LABELS } from '../../utils/projectPlan';
import { descendantIds } from '../../utils/taskTree';
import TaskIcon from '../tasks/TaskIcon';

const SHOW = 8;

/**
 * "Waits for": the tasks this one can't start (or finish) before. Pick from
 * the rest of the project; the server refuses a link that would make a loop.
 */
const DependsPanel: React.FC<{ item: TaskItem; readOnly: boolean; onClose: () => void; onBack: () => void }> = ({
  item,
  readOnly,
  onClose,
  onBack,
}) => {
  const { itemsForList, updateItem } = useTasks();
  const all = itemsForList(item.list_id);
  const byId = useMemo(() => new Map(all.map((i) => [i.id, i])), [all]);
  const [deps, setDeps] = useState<Predecessor[]>(item.predecessors || []);
  const [query, setQuery] = useState('');

  // Not itself, its own branch, or anything already picked.
  const off = useMemo(() => {
    const out = new Set<number>([item.id, ...descendantIds(all, item.id), ...deps.map((d) => d.id)]);
    for (let p = item.parent_id ? byId.get(item.parent_id) : undefined; p; p = p.parent_id ? byId.get(p.parent_id) : undefined) out.add(p.id);
    return out;
  }, [all, byId, item, deps]);
  const q = query.trim().toLowerCase();
  const candidates = all
    .filter((i) => i.id > 0 && !off.has(i.id) && i.text && (!q || i.text.toLowerCase().includes(q)))
    .slice(0, SHOW);
  const where = (i: TaskItem) => (i.parent_id ? byId.get(i.parent_id)?.text : '') || '';

  const changed = JSON.stringify(deps) !== JSON.stringify(item.predecessors || []);
  const save = () => {
    if (changed) updateItem(item.id, { predecessors: deps });
    onClose();
  };
  const set = (id: number, patch: Partial<Predecessor>) => setDeps((prev) => prev.map((d) => (d.id === id ? { ...d, ...patch } : d)));

  return (
    <div className="tk-menu-panel tp-deps">
      <div className="tk-menu-title tp-deps-title">
        <button
          type="button"
          className="tk-icon-btn tk-icon-btn-sm"
          onClick={() => {
            if (changed && !readOnly) updateItem(item.id, { predecessors: deps });
            onBack();
          }}
          aria-label="Back to Schedule"
        >
          <TaskIcon name="chevron-left" size={14} />
        </button>
        Waits for
      </div>

      {deps.length === 0 && <p className="tk-menu-hint">Nothing yet — it can start whenever. Pick what has to happen first.</p>}
      <ul className="tp-deps-list">
        {deps.map((d) => {
          const other = byId.get(d.id);
          return (
            <li key={d.id}>
              <span className="tp-deps-name" title={other?.text}>{other?.text || 'A deleted task'}</span>
              <div className="tp-deps-opts">
                <select className="tk-input" value={d.type} onChange={(e) => set(d.id, { type: e.target.value as Predecessor['type'] })} disabled={readOnly}>
                  {(Object.keys(DEP_LABELS) as Predecessor['type'][]).map((t) => (
                    <option key={t} value={t}>{DEP_LABELS[t]}</option>
                  ))}
                </select>
                <label className="tp-deps-lag" title="Days to wait after that (negative = overlap)">
                  +
                  <input type="number" className="tk-input" value={d.lag} onChange={(e) => set(d.id, { lag: Math.round(Number(e.target.value)) || 0 })} disabled={readOnly} />
                  d
                </label>
                {!readOnly && (
                  <button type="button" className="tk-icon-btn tk-icon-btn-sm" onClick={() => setDeps((prev) => prev.filter((x) => x.id !== d.id))} aria-label="Remove">
                    <TaskIcon name="close" size={12} />
                  </button>
                )}
              </div>
            </li>
          );
        })}
      </ul>

      {!readOnly && (
        <>
          <label className="tk-search tp-deps-search">
            <TaskIcon name="search" size={14} />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a task it waits for" aria-label="Find a task" />
          </label>
          <div className="tp-deps-results">
            {candidates.map((c) => (
              <button
                key={c.id}
                type="button"
                className="tk-menu-item"
                onClick={() => {
                  setDeps((prev) => [...prev, { id: c.id, type: 'FS', lag: 0 }]);
                  setQuery('');
                }}
              >
                <TaskIcon name="plus" size={13} />
                <span className="tp-deps-cand">
                  {c.text}
                  {where(c) && <small className="tk-muted"> · {where(c)}</small>}
                </span>
              </button>
            ))}
            {q && !candidates.length && <p className="tk-menu-hint">No other task matches.</p>}
          </div>
          <div className="tk-menu-actions">
            <button type="button" className="tk-btn tk-btn-primary" onClick={save}>{changed ? 'Save' : 'Done'}</button>
          </div>
        </>
      )}
    </div>
  );
};

export default DependsPanel;
